import { z } from "zod";

/** One of their Google calendars, for picking where visits go and which ones block booking. */
export interface GoogleCalendarChoice {
  id: string;
  name: string;
  primary: boolean;
  /** Visits can go on it. */
  canWrite: boolean;
  /** Its busy times can be read. */
  canRead: boolean;
  color: string | null;
}

/** A staff member's calendar sync (Account → My calendar; managers on the staff page). */
export interface StaffCalendarView {
  /** The clinic's Google Workspace is connected (and its domain, e.g. vaultedvitality.com). */
  google: { domain: string } | null;
  googleEmail: string | null;
  pushGoogle: boolean;
  /** Which Google calendar gets the visits (null = their main calendar). */
  pushCalendarId: string | null;
  googleSyncedAt: string | null;
  googleError: string | null;
  /** A subscribe link exists (its address was shown once). */
  hasFeed: boolean;
  feedCreatedAt: string | null;
  /** Busy times on their own calendars block booking (optional). */
  blockBusy: boolean;
  /** Their Google calendars whose busy times count, with minutes blocked before and after each event. */
  busyCalendars: BusyCalendarChoice[];
  /** Private iCal addresses added (never shown again; only where they're from), with minutes before and after. */
  icals: { id: string; host: string; addedAt: string; before: number; after: number }[];
  busySyncedAt: string | null;
  busyError: string | null;
}

export const staffCalendarSchema = z.object({
  googleEmail: z
    .string()
    .trim()
    .toLowerCase()
    .transform((v) => v || null)
    .pipe(z.string().email("Enter their Google address").nullable()),
  pushGoogle: z.boolean(),
  /** Left out: keep the one chosen. */
  pushCalendarId: z.string().trim().min(1).max(300).nullable().optional(),
});
export type StaffCalendarInput = z.input<typeof staffCalendarSchema>;

/** Minutes a calendar's busy time is stretched by, before and after each event. */
export const BUSY_BUFFER_CHOICES = [0, 15, 30, 45, 60] as const;
export const DEFAULT_BUSY_BUFFER = 30;
const buffer = z.coerce
  .number()
  .int()
  .refine((n) => (BUSY_BUFFER_CHOICES as readonly number[]).includes(n), "Pick 0, 15, 30, 45, or 60 minutes");

export interface BusyCalendarChoice {
  id: string;
  before: number;
  after: number;
}

export const busyBufferSchema = z.object({ before: buffer, after: buffer });

export const staffBusySchema = z.object({
  on: z.boolean(),
  /** Their Google calendars whose busy times count, each with its minutes before and after. */
  googleCalendars: z
    .array(z.object({ id: z.string().trim().min(1).max(300), before: buffer.default(DEFAULT_BUSY_BUFFER), after: buffer.default(DEFAULT_BUSY_BUFFER) }))
    .max(20)
    .default([]),
});
export type StaffBusyInput = z.input<typeof staffBusySchema>;

export const MAX_ICAL_ADDRESSES = 5;
export const staffIcalSchema = z.object({
  url: z.string().trim().min(1, "Paste the calendar's private address").max(2000),
  before: buffer.default(DEFAULT_BUSY_BUFFER),
  after: buffer.default(DEFAULT_BUSY_BUFFER),
});
